// frontend/src/store/authStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

// Shared across all callers: a dead session must produce exactly one
// refresh attempt and one logout, not one per failed request.
let inFlightRefresh = null;
let inFlightLogout  = null;

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      permissions: null, // { [module]: { can_view, can_create, can_edit, can_delete, hidden_actions } }

      // Load ABAC permissions for the logged-in user
      loadPermissions: async () => {
        try {
          const res = await api.get('/roles/my-permissions');
          set({ permissions: res.data.data });
        } catch (_) {}
      },

      // Check if user can perform an action on a module
      // admin always returns true; falls back to true if permissions not loaded yet
      can: (module, action = 'view') => {
        const { user, permissions } = get();
        if (!user) return false;
        if (user.role === 'admin' || user.role === 'super_admin') return true;
        if (!permissions) return true; // not yet loaded — optimistic
        return !!permissions[module]?.[`can_${action}`];
      },

      // Check if a granular action is hidden for this role
      isActionHidden: (module, action) => {
        const { user, permissions } = get();
        if (!user || user.role === 'admin' || user.role === 'super_admin') return false;
        return (permissions?.[module]?.hidden_actions || []).includes(action);
      },

      login: async (email, password, turnstileToken) => {
        set({ isLoading: true });
        try {
          const res = await api.post('/auth/login', { email, password, turnstileToken });
          const data = res.data.data;
          // 2FA required — caller handles the redirect
          if (data.requires2FA) {
            set({ isLoading: false });
            return { success: true, requires2FA: true, challengeToken: data.challengeToken, userId: data.userId };
          }
          const { user, accessToken, refreshToken, autoClockIn } = data;
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          set({ user, accessToken, refreshToken, isAuthenticated: true, isLoading: false });
          // Load ABAC permissions after login
          get().loadPermissions().catch(() => {});
          // Warm the SW cache with today + tomorrow data for offline use
          import('../utils/prefetchOfflineData').then(({ prefetchOfflineData }) =>
            prefetchOfflineData().catch(() => {})
          );
          return { success: true, autoClockIn: autoClockIn || false };
        } catch (err) {
          set({ isLoading: false });
          return { success: false, message: err.response?.data?.message || 'Login failed' };
        }
      },

      setAuth: ({ accessToken, refreshToken, user }) => {
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        set({ user, accessToken, refreshToken, isAuthenticated: true });
        get().loadPermissions().catch(() => {});
      },

      logout: async () => {
        // Guard against the logout storm: when a session dies, every in-flight
        // request fails at once and each one used to POST its own /auth/logout.
        // Production logs showed eight in a quarter of a second.
        if (inFlightLogout) return inFlightLogout;
        inFlightLogout = (async () => {
          try { await api.post('/auth/logout'); } catch (e) {}
          delete api.defaults.headers.common['Authorization'];
          set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false, permissions: null });
        })().finally(() => { inFlightLogout = null; });
        return inFlightLogout;
      },

      /**
       * Single-flight token refresh.
       *
       * Every request that got a 401 used to call this independently, so an
       * expired refresh token produced nine parallel /auth/refresh calls and a
       * logout each — which burned the entire login rate-limit budget in under
       * a second and locked the user out of their next login attempt. Now the
       * first caller does the work and the rest await the same promise.
       */
      refreshAccessToken: async () => {
        if (inFlightRefresh) return inFlightRefresh;

        const { refreshToken } = get();
        if (!refreshToken) return false;

        inFlightRefresh = (async () => {
          try {
            const res = await api.post('/auth/refresh', { refreshToken });
            const { accessToken, refreshToken: newRefresh } = res.data.data;
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
            set({ accessToken, refreshToken: newRefresh });
            return true;
          } catch (e) {
            await get().logout();
            return false;
          }
        })().finally(() => { inFlightRefresh = null; });

        return inFlightRefresh;
      },

      setToken: (token) => {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      },

      hasModule: (module) => {
        const hotel = require('./hotelContext').useHotelContext.getState().hotel; return hotel?.modules?.[module] ?? false;
      },

      hasRole: (...roles) => {
        return roles.includes(get().user?.role);
      },
    }),
    {
      name: 'staynivo-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        permissions: state.permissions,
      }),
    }
  )
);
